import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from 'react-query'

import PasswordRequirements from '../PasswordRequirements/PasswordRequirements'
import * as passwordValidators from '../../shared/passwordValidators'

import Container from '../Container/Container'

import * as styles from './Signup.m.css'

const queryParams = new URLSearchParams(window.location.search)
const signupCode = queryParams.get('c')

let debounceTimer: number
export const debounce = (
  fn: () => void,
  delay: number,
  debounceTimer: number,
) => {
  window.clearTimeout(debounceTimer)
  return window.setTimeout(fn, delay)
}

async function isUsernameAvailable(username: string): Promise<boolean> {
  const response = await fetch(`/auth/isAvailableUsername/${username}`)
  const isValid = await response.text()

  return isValid === 'true'
}

async function isSignupCodeValid(): Promise<string> {
  const response = await fetch(`/auth/isValidSignupCode/${signupCode}`)
  return await response.text()
}

const SignUp: React.FC = (): React.ReactElement => {
  const navigate = useNavigate()
  const validCodeQuery = useQuery('validCode', isSignupCodeValid, {
    staleTime: Infinity,
  })

  if (!signupCode) {
    navigate('/invalid-sign-up')
  }

  const [username, setUsername] = useState('')
  const [usernameAvailable, setUsernameAvailable] = useState<boolean>(true)
  const [usernameValid, setUsernameValid] = useState<boolean>(true)
  const [email, setEmail] = useState('')
  const [emailValid, setEmailValid] = useState<boolean>(true)
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [userCreated, setUserCreated] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')

  const usernameChangedHandler: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    const newUsername = e.target.value
    setUsernameAvailable(true)
    setUsernameValid(true)

    if (newUsername.length === 0) {
      setUsernameAvailable(true)
      return
    }

    if (newUsername.length < 4) {
      setUsernameValid(false)
      return
    }

    if (!/^[a-zA-Z0-9_]*$/.test(newUsername)) {
      setUsernameValid(false)
      return
    }

    debounceTimer = debounce(
      async () => {
        if (newUsername === '') {
          setUsernameAvailable(true)
          return
        }
        const isValid = await isUsernameAvailable(newUsername)
        setUsernameAvailable(isValid)

        if (isValid) {
          setUsernameValid(true)
          setUsername(newUsername)
        }
      },
      500,
      debounceTimer,
    )
  }

  const emailChangedHandler: React.ChangeEventHandler<HTMLInputElement> = (
    e,
  ) => {
    if (e.target.value === '') {
      setEmailValid(true)
      return
    }

    // at least one character before the @ sign, at least one after, at least
    // one dot after that, and at least one character after the dot.
    const emailValid = /^.+@.+\..+$/.test(e.target.value)
    setEmailValid(emailValid)

    if (emailValid) {
      setEmail(e.target.value)
    }
  }

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = async (e) => {
    e.preventDefault()

    const response = await fetch('/auth/sign-up', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        username,
        email,
        password,
        signupCode,
      }),
    })

    if (response.ok) {
      setUserCreated(true)
      setTimeout(() => {
        navigate('/')
      }, 3000)
    } else {
      console.error('Error:', response.status, response.statusText)
      if (response.statusText === 'Invalid Fields(s)') {
        setErrorMessage('One ore more fields was invalid.')
      } else {
        setErrorMessage('An error occurred. Please try again later.')
      }
    }
    return false
  }

  const passwordValid = passwordValidators.isValidPassword(password)

  const formValid =
    usernameValid &&
    usernameAvailable &&
    emailValid &&
    password === confirmPassword &&
    password.length > 0 &&
    passwordValid

  const contents =
    validCodeQuery.data === 'true' ? (
      <form onSubmit={handleSubmit} className={styles.formContainer}>
        <input
          type="text"
          name="username"
          placeholder="Username"
          onChange={usernameChangedHandler}
          className={usernameAvailable ? '' : styles.invalid}
        />
        {!usernameAvailable && (
          <div className={styles.error}>Username is already taken.</div>
        )}
        {!usernameValid && (
          <div className={styles.error}>
            Username must be at least 4 characters long and may only contain
            letters, numbers, and _
          </div>
        )}
        <input
          type="email"
          name="email"
          placeholder="Email"
          onChange={emailChangedHandler}
          className={emailValid ? '' : styles.invalid}
        />
        {!emailValid && (
          <div className={styles.error}>Must be a valid email address.</div>
        )}
        <input
          type="password"
          name="password"
          placeholder="Password"
          onChange={(e) => setPassword(e.target.value)}
          className={
            passwordValid || password.length === 0 ? '' : styles.invalid
          }
        />
        <input
          type="password"
          name="password"
          placeholder="Confirm Password"
          onChange={(e) => setConfirmPassword(e.target.value)}
          className={
            password === confirmPassword || password.length === 0
              ? ''
              : styles.invalid
          }
        />
        <PasswordRequirements
          password={password}
          confirmPassword={confirmPassword}
        />
        <button type="submit" disabled={!formValid || userCreated}>
          Sign Up
        </button>
        {userCreated && (
          <div className={styles.valid}>
            Account created. You will be redirected to{' '}
            <a href="/">the login page.</a> momentarily.
          </div>
        )}
        {errorMessage && <div className={styles.error}>{errorMessage}</div>}
      </form>
    ) : (
      <div className={styles.invalidSignupCode}>
        <h2>Invalid Sign Up Code</h2>
        <div>
          The provided sign up code has either expired, no longer valid, or has
          exceeded the maximum number of uses. Please contact your rep at PPS to
          have a new sign up email sent.
        </div>
      </div>
    )

  return (
    <Container>
      <div>
        <h1 className={styles.heading}>Sign Up</h1>
        {contents}
      </div>
    </Container>
  )
}

export default SignUp
